import React from 'react';
import { Link } from 'react-router-dom';
import { formatDateShort, formatPrice } from '../../lib/format';
import type { FixtureSummary } from '../../lib/types';
import {
  CalendarIcon,
  ArrowRightIcon,
  ScaleIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';

interface FixtureCardProps {
  fixture: FixtureSummary;
  showLeague?: boolean;
  matchType?: 'Home' | 'Away';
}

function TicketedBadge({ fixture }: { fixture: FixtureSummary }) {
  const cheaper = fixture.ticketed_cheaper || 0;
  const more = fixture.ticketed_more_expensive || 0;
  const total = cheaper + more + (fixture.ticketed_same || 0);

  if (total === 0) return null;

  if (cheaper > 0 && more === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
        <CheckCircleIcon className="w-3 h-3" />
        Ticketed cheapest
      </span>
    );
  }
  if (more > 0 && cheaper === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-400 border border-rose-500/20">
        <ArrowTrendingUpIcon className="w-3 h-3" />
        Competitors cheaper
      </span>
    );
  }
  // Mixed — show ratio
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20">
      <ScaleIcon className="w-3 h-3" />
      {cheaper}↓ {more}↑
    </span>
  );
}

export default function FixtureCard({ fixture, showLeague = true, matchType }: FixtureCardProps) {
  const minPrice = fixture.min_price ? parseFloat(String(fixture.min_price)) : null;
  const maxPrice = fixture.max_price ? parseFloat(String(fixture.max_price)) : null;
  const savings = minPrice && maxPrice && maxPrice > minPrice
    ? maxPrice - minPrice
    : null;
  const hasComparisons = fixture.group_count > 0 && fixture.supplier_count >= 2;

  return (
    <Link
      to={`/match/${fixture.match_id}`}
      className="card p-4 sm:p-5 hover:bg-slate-700/30 transition-all group block"
    >
      {/* Desktop layout */}
      <div className="hidden sm:flex items-center gap-4">
        {/* Team logos */}
        <div className="flex items-center -space-x-2 shrink-0">
          <img
            src={fixture.home_logo}
            alt={fixture.home_team}
            className="w-10 h-10 rounded-full bg-slate-700 object-contain p-1 ring-2 ring-slate-800"
          />
          <img
            src={fixture.away_logo}
            alt={fixture.away_team}
            className="w-10 h-10 rounded-full bg-slate-700 object-contain p-1 ring-2 ring-slate-800"
          />
        </div>

        {/* Match info */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-slate-100 font-semibold truncate">
              {fixture.home_team} v {fixture.away_team}
            </h3>
            {matchType && (
              <span className={`badge text-[10px] ${matchType === 'Home' ? 'badge-home' : 'badge-away'}`}>
                {matchType}
              </span>
            )}
            <TicketedBadge fixture={fixture} />
          </div>
          <div className="flex items-center gap-3 mt-1 text-sm text-slate-400">
            <span className="flex items-center gap-1">
              <CalendarIcon className="w-3.5 h-3.5" />
              {formatDateShort(fixture.match_date)}
            </span>
            {showLeague && fixture.league_name && (
              <span className="text-slate-500">{fixture.league_name}</span>
            )}
          </div>
        </div>

        {/* Price comparison summary */}
        <div className="flex items-center gap-5 shrink-0">
          {hasComparisons ? (
            <div className="text-right">
              <div className="flex items-center gap-2 justify-end">
                {minPrice && (
                  <span className="text-emerald-400 font-bold text-lg">
                    {formatPrice(minPrice)}
                  </span>
                )}
                {maxPrice && minPrice && maxPrice !== minPrice && (
                  <>
                    <span className="text-slate-600">—</span>
                    <span className="text-rose-400/80 font-semibold text-sm">
                      {formatPrice(maxPrice)}
                    </span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2 mt-0.5 justify-end">
                {savings && savings > 0 && (
                  <span className="text-xs text-emerald-400/70">
                    Save up to {formatPrice(savings)}
                  </span>
                )}
                <span className="flex items-center gap-1 text-xs text-slate-500">
                  <ScaleIcon className="w-3 h-3" />
                  {fixture.supplier_count} suppliers
                </span>
              </div>
            </div>
          ) : (
            <span className="text-sm text-slate-500">No comparisons</span>
          )}
          <ArrowRightIcon className="w-4 h-4 text-slate-600 group-hover:text-emerald-400 transition-colors" />
        </div>
      </div>

      {/* Mobile layout — stacked */}
      <div className="sm:hidden">
        <div className="flex items-center gap-3">
          <div className="flex items-center -space-x-2 shrink-0">
            <img
              src={fixture.home_logo}
              alt={fixture.home_team}
              className="w-8 h-8 rounded-full bg-slate-700 object-contain p-0.5 ring-2 ring-slate-800"
            />
            <img
              src={fixture.away_logo}
              alt={fixture.away_team}
              className="w-8 h-8 rounded-full bg-slate-700 object-contain p-0.5 ring-2 ring-slate-800"
            />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-slate-100 font-semibold text-sm truncate">
              {fixture.home_team} v {fixture.away_team}
            </h3>
          </div>
          {matchType && (
            <span className={`badge text-[10px] ${matchType === 'Home' ? 'badge-home' : 'badge-away'}`}>
              {matchType}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between mt-2 pl-[52px]">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span className="flex items-center gap-1">
              <CalendarIcon className="w-3 h-3" />
              {formatDateShort(fixture.match_date)}
            </span>
            <TicketedBadge fixture={fixture} />
          </div>
          {hasComparisons && minPrice ? (
            <span className="text-emerald-400 font-bold text-sm">
              {formatPrice(minPrice)}
            </span>
          ) : (
            <span className="text-xs text-slate-500">—</span>
          )}
        </div>
      </div>
    </Link>
  );
}
